import { Injectable } from '@angular/core';
import { CreateEffectMetadata } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { SciGuaduaHttpRequestHandler } from '@scifamek/guadua';
import { Observable } from 'rxjs';
import { createEntityEffects } from '../../../shared/base';
import {
  CreateBookingCommand,
  UpdateBookingCommand,
  AssignVendorToBookingCommand,
  GetAllBookingsByStatusCommand,
  GetAllBookingsBetweenDatesCommand,
  GetAllBookingsByVendorDocumentCommand,
  GetAllBookingsByResidentAndStatusCommand,
  GetAllBookingsBetweenDatesAndBuildingIdCommand,
  GetAllBookingsByBuildingIdCommand,
  ValidateBookingAccessCodeCommand,
  DownloadBookingsReportByBuildingIdCommand,
  UpdateBookingProcessStatusCommand,
  GetAllBookingsByResidentCommand,
  AssignVendorCompanyToBookingCommand,
} from './booking.commands';
import {
  CreatedBookingEvent,
  UpdatedBookingEvent,
  AssignedVendorToBookingEvent,
  GotAllBookingsByStatusEvent,
  GotAllBookingsBetweenDatesEvent,
  GotAllBookingsByVendorDocumentEvent,
  GotAllBookingsByResidentAndStatusEvent,
  GotAllBookingsBetweenDatesAndBuildingIdEvent,
  GotAllBookingsByBuildingIdEvent,
  ValidatedBookingAccessCodeEvent,
  DownloadedBookingsReportByBuildingIdEvent,
  UpdatedBookingProcessStatusEvent,
  InvalidBookingAccessCodeEvent,
  GotAllBookingsByResidentEvent,
  AssignedVendorCompanyEvent,
} from './booking.events';

@Injectable()
export class BookingEffects {
  prefix = 'booking';
  createBookingCommand: Observable<Action<string>> & CreateEffectMetadata;
  updateBookingCommand: Observable<Action<string>> & CreateEffectMetadata;
  assignVendorToBookingCommand: Observable<Action<string>> & CreateEffectMetadata;
  assignVendorCompanyToBookingCommand: Observable<Action<string>> & CreateEffectMetadata;
  getAllBookingsByStatusCommand: Observable<Action<string>> & CreateEffectMetadata;
  getAllBookingsBetweenDatesCommand: Observable<Action<string>> & CreateEffectMetadata;
  getAllBookingsByVendorDocumentCommand: Observable<Action<string>> & CreateEffectMetadata;
  getAllBookingsByResidentAndStatusCommand: Observable<Action<string>> & CreateEffectMetadata;
  getAllBookingsBetweenDatesAndBuildingIdCommand: Observable<Action<string>> & CreateEffectMetadata;
  getAllBookingsByBuildingIdCommand: Observable<Action<string>> & CreateEffectMetadata;
  getAllBookingsByResidentCommand: Observable<Action<string>> & CreateEffectMetadata;
  validateBookingAccessCodeCommand: Observable<Action<string>> & CreateEffectMetadata;
  downloadBookingsReportByBuildingIdCommand: Observable<Action<string>> & CreateEffectMetadata;
  updateBookingProcessStatusCommand: Observable<Action<string>> & CreateEffectMetadata;

  constructor(private sciGuaduaHttpRequestHandler: SciGuaduaHttpRequestHandler) {
    this.createBookingCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      CreateBookingCommand,
      CreatedBookingEvent,
      this.prefix,
    );
    this.updateBookingCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      UpdateBookingCommand,
      UpdatedBookingEvent,
      this.prefix,
    );
    this.assignVendorToBookingCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      AssignVendorToBookingCommand,
      AssignedVendorToBookingEvent,
      this.prefix,
    );
    this.assignVendorCompanyToBookingCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      AssignVendorCompanyToBookingCommand,
      AssignedVendorCompanyEvent,
      this.prefix,
    );
    this.getAllBookingsByStatusCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      GetAllBookingsByStatusCommand,
      GotAllBookingsByStatusEvent,
      this.prefix,
    );
    this.getAllBookingsBetweenDatesCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      GetAllBookingsBetweenDatesCommand,
      GotAllBookingsBetweenDatesEvent,
      this.prefix,
    );
    this.getAllBookingsByVendorDocumentCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      GetAllBookingsByVendorDocumentCommand,
      GotAllBookingsByVendorDocumentEvent,
      this.prefix,
    );
    this.getAllBookingsByResidentAndStatusCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      GetAllBookingsByResidentAndStatusCommand,
      GotAllBookingsByResidentAndStatusEvent,
      this.prefix,
    );
    this.getAllBookingsBetweenDatesAndBuildingIdCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      GetAllBookingsBetweenDatesAndBuildingIdCommand,
      GotAllBookingsBetweenDatesAndBuildingIdEvent,
      this.prefix,
    );
    this.getAllBookingsByBuildingIdCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      GetAllBookingsByBuildingIdCommand,
      GotAllBookingsByBuildingIdEvent,
      this.prefix,
    );
    this.getAllBookingsByResidentCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      GetAllBookingsByResidentCommand,
      GotAllBookingsByResidentEvent,
      this.prefix,
    );
    this.validateBookingAccessCodeCommand = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: ValidateBookingAccessCodeCommand,
      prefix: 'booking',
      others: (httpResponse: any, commandData: any) => {
        if (httpResponse.meta?.code === 'Booking:InvalidBookingAccessCodeDomainEvent') {
          return [InvalidBookingAccessCodeEvent({ error: 'Invalid access code' })];
        }
        return [ValidatedBookingAccessCodeEvent({ entities: httpResponse.data || [] })];
      },
    });
    this.downloadBookingsReportByBuildingIdCommand = this.sciGuaduaHttpRequestHandler.handleEvent({
      command: DownloadBookingsReportByBuildingIdCommand,
      prefix: 'booking',
      others: (httpResponse: any, commandData: any) => {
        const bufferData = httpResponse.data;
        if (bufferData && bufferData.type === 'Buffer' && bufferData.data) {
          const uint8Array = new Uint8Array(bufferData.data);
          const blob = new Blob([uint8Array], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'bookings-report.xlsx';
          link.click();
          window.URL.revokeObjectURL(url);
        }
        return [DownloadedBookingsReportByBuildingIdEvent({ entities: [] })];
      },
    });
    this.updateBookingProcessStatusCommand = createEntityEffects(
      sciGuaduaHttpRequestHandler,
      UpdateBookingProcessStatusCommand,
      UpdatedBookingProcessStatusEvent,
      this.prefix,
    );
  }
}
